import { Component } from '@theme/component';
import { readBundleComponents } from '@theme/bundle-personalization-data';
import { normalizePersonalizationJsonString, parseLineItemPersonalizationMap } from '@theme/line-item-personalization-cart';

function element(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function imageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return '';
  try {
    const url = new URL(value, window.location.origin);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
}

/** Display only: never modifies the cart, its PID, or saved personalization. */
class BundleCartPersonalization extends Component {
  #sourceObserver = new MutationObserver(() => this.render());

  observeSource() {
    // Cart section morphs can clear generated rows even when the JSON is unchanged.
    this.#sourceObserver.observe(this, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-rendered', 'data-drawer'],
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.observeSource();
    queueMicrotask(() => this.render());
  }

  disconnectedCallback() {
    this.#sourceObserver.disconnect();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.render();
  }

  render() {
    if (!this.isConnected) return;
    const content = this.querySelector('[data-bundle-cart-content]');
    if (!content) return;
    // Do not observe our own generated markup and trigger another render.
    this.#sourceObserver.disconnect();
    this.removeAttribute('data-rendered');
    content.replaceChildren();
    try {
      const data = JSON.parse(this.querySelector('[data-bundle-cart-data]').textContent);
      // Liquid cart attributes may be HTML-escaped, just like single-item data.
      let map = data.map;
      if (typeof map === 'string') {
        try { map = JSON.parse(map); }
        catch { map = parseLineItemPersonalizationMap(map); }
      }
      const components = readBundleComponents(map?.[data.pid]) || readBundleComponents(data.legacy) ||
        readBundleComponents(normalizePersonalizationJsonString(data.legacy));
      if (!components) return;
      const sections = [];
      for (const component of components) {
        const child = data.children.find((child) =>
          child.variantId === String(component.variant_id).split('/').pop() &&
          child.productId === String(component.product_id).split('/').pop()
        );
        // Do not associate saved choices with a different child after a cart update.
        if (!child) return;
        const section = this.renderChild(component, child);
        if (section) sections.push(section);
      }
      if (!sections.length) return;
      content.replaceChildren(...sections);
      this.setAttribute('data-rendered', '');
    } catch (error) {
      // Leave the existing summaries available if old or malformed data cannot be read.
      console.warn('Could not display bundle personalization:', error);
    } finally {
      this.observeSource();
    }
  }

  renderChild(component, child) {
    const attributes = new Map(component.attributes.filter((attribute) => attribute?.key).map(({ key, value }) => [key, value]));
    const skipped = attributes.get('_skip_personalization') === 'true';
    const design = child.designs.find((design) => design.id === String(attributes.get('_selected-id')));
    const preview = imageUrl(attributes.get('Design'));
    const colorKey = ['Thread Color', 'Vinyl Color', 'Foil Color'].find((key) => attributes.get(key));
    const text = attributes.get('_selected-letter-type');
    const section = element('section', 'cart-items__bundle-personalization-item');
    const title = child.quantity > 1 ? `${child.title} × ${child.quantity}` : child.title;
    section.append(element('h4', 'cart-items__bundle-personalization-title', title));
    const details = element('dl', 'cart-items__bundle-personalization-details');
    section.append(details);
    const addRow = (label) => {
      const row = element('div', 'cart-items__properties');
      const value = document.createElement('dd');
      row.append(element('dt', 'cart-items__properties-title', `${label}:`), document.createTextNode(' '), value);
      details.append(row);
      return value;
    };

    for (const option of child.sizeOptions || []) {
      addRow(option.name).append(element('span', 'cart-items__properties-value', option.value));
    }
    if (skipped) {
      const row = element('div', 'cart-items__properties');
      row.append(element('dd', 'cart-items__properties-value--no-personalization', this.dataset.noPersonalization));
      details.append(row);
      return section;
    }

    if (preview || design?.name) {
      const value = addRow('Design');
      if (preview) {
        const wrapper = element('div', `cart-items__properties-image-container${this.dataset.drawer === 'true' ? ' cart-items__properties-image-container--drawer' : ''}`);
        const image = element('img', 'cart-items__properties-image');
        image.src = preview;
        image.alt = design?.name || 'Design';
        image.width = 30;
        image.height = 30;
        image.loading = 'lazy';
        wrapper.append(image);
        value.append(wrapper);
      } else {
        // A catalog icon is not the customer's generated monogram image.
        value.append(element('span', 'cart-items__properties-value', design.name));
      }
    }
    if (colorKey && !child.hideColor) {
      const value = addRow(child.colorLabel || colorKey);
      value.append(element('span', 'cart-items__properties-value', attributes.get(colorKey)));
      const hex = attributes.get('_swatch-color');
      if (typeof hex === 'string' && /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i.test(hex)) {
        const swatch = element('span', 'cart-items__properties-swatch-color');
        swatch.setAttribute('aria-hidden', 'true');
        swatch.style.setProperty('--swatch-color', hex);
        value.append(swatch);
      }
    }
    if (text && typeof text.value === 'string' && text.value) {
      const value = addRow(design?.letterLabel || text.label || 'Personalization');
      value.append(element('span', 'cart-items__properties-value', text.value));
    }
    return section;
  }
}

if (!customElements.get('bundle-cart-personalization')) {
  customElements.define('bundle-cart-personalization', BundleCartPersonalization);
}
